import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Home, Car, Building2, ChevronRight, Phone, Mail, MapPin, Star, CheckCircle, Users, Award, Heart, ArrowRight, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import { useParallax, RevealSection, StaggerReveal } from '../hooks/useScrollEffects';

const API = process.env.REACT_APP_BACKEND_URL;

const YouTubeFacade = ({ videoId, title }) => {
  const [loaded, setLoaded] = useState(false);
  if (loaded) {
    return (
      <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
        <div className="relative" style={{ paddingBottom: '177.78%' }}>
          <iframe
            className="absolute top-0 left-0 w-full h-full"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </Card>
    );
  }
  return (
    <Card
      className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group"
      onClick={() => setLoaded(true)}
      data-testid={`video-facade-${videoId}`}
    >
      <div className="relative" style={{ paddingBottom: '177.78%' }}>
        <img
          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
          alt={title}
          className="absolute top-0 left-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#d32f2f] rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>
    </Card>
  );
};

const HomePage = () => {
  const [activeTab, setActiveTab] = useState('family');
  const [contactForm, setContactForm] = useState({ firstName: '', lastName: '', email: '', phone: '', insuranceType: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [heroRef, heroOffset] = useParallax(0.3);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactForm.firstName || !contactForm.email || !contactForm.message) return toast.error('Please fill in name, email, and message');
    setContactSubmitting(true);
    fetch(`${API}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: `${contactForm.firstName} ${contactForm.lastName}`.trim(),
        email: contactForm.email,
        phone: contactForm.phone,
        insurance_type: contactForm.insuranceType,
        message: contactForm.message,
      }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => {
        toast.success('Message sent! We\'ll get back to you soon.');
        setContactForm({ firstName: '', lastName: '', email: '', phone: '', insuranceType: '', message: '' });
      })
      .catch(() => toast.error('Something went wrong. Please call us at 706-434-8184.'))
      .finally(() => setContactSubmitting(false));
  };

  const services = [
    {
      icon: Home,
      title: 'Home Insurance',
      description: 'Compare homeowners insurance from 30+ carriers in Augusta GA, North Augusta SC & the CSRA. Protect your property from fire, storms, theft & liability.',
      image: 'https://cdn.prod.website-files.com/68b1bffe580cead7ff4a6957/68d4258b7225db02456df89d_Home.avif',
      link: '/services/home-insurance'
    },
    {
      icon: Car,
      title: 'Auto Insurance',
      description: 'Affordable car insurance for SC & GA drivers. Full coverage, liability, collision & comprehensive from 30+ carriers. CSRA drivers save 23% on average.',
      image: 'https://cdn.prod.website-files.com/68b1bffe580cead7ff4a6957/68d42589f6f1e8d33eafe462_Auto.avif',
      link: '/services/auto-insurance'
    },
    {
      icon: Building2,
      title: 'Business Insurance',
      description: 'Commercial insurance for Augusta GA & SC businesses. General liability, BOP, workers comp & more. Tailored solutions from a veteran-owned agency.',
      image: 'https://cdn.prod.website-files.com/68b1bffe580cead7ff4a6957/68d4258b6115d3343468bba8_Business.avif',
      link: '/services/business-insurance'
    }
  ];

  const testimonials = [
    {
      name: 'Lakelynn Pfluke',
      image: 'https://i.pravatar.cc/150?img=1',
      rating: 5,
      text: 'I worked with Adam for a bundle package as we are in the process of purchasing our first house. Adam was present, kind and very knowledgeable. Would rate him 1000k stars if I could!!'
    },
    {
      name: 'David Hoffman',
      image: 'https://i.pravatar.cc/150?img=2',
      rating: 5,
      text: 'I called Adam about Home insurance he reached back out the next afternoon with the quote. I find it to be a fair quote. Very professional talking, Very knowledgeable. I would say a fast response and fair priced. I would definitely recommend him to others'
    },
    {
      name: 'Jessica Soto',
      image: 'https://i.pravatar.cc/150?img=3',
      rating: 5,
      text: 'I received a quick insurance quote for my home and they even saved me money by bundling my car insurance! The staff is very responsive, professional, polite and answered all my questions!'
    },
    {
      name: 'Emily Gaillard',
      image: 'https://i.pravatar.cc/150?img=4',
      rating: 5,
      text: 'Adam was great at helping us get the homeowners insurance we need! He was very professional and friendly! Highly recommend!'
    },
    {
      name: 'Rachel',
      image: 'https://i.pravatar.cc/150?img=5',
      rating: 5,
      text: 'My agent was quick, helpful, and friendly. I had a million questions and she answered them all with candor and knowledge that I appreciated.'
    },
    {
      name: 'Cyril Johnson',
      image: 'https://i.pravatar.cc/150?img=6',
      rating: 5,
      text: 'Allyson was requested for a quote on a second vacant house I own. The quote was emailed to me for my review. I approved the quote and paid online with no problems.'
    },
    {
      name: 'Michael Thompson',
      image: 'https://i.pravatar.cc/150?img=7',
      rating: 5,
      text: 'Outstanding service from start to finish! Total Quality Insurance made switching our policies seamless. Saved us over $200/month on our bundle. Highly recommend their team!'
    },
    {
      name: 'Sarah Martinez',
      image: 'https://i.pravatar.cc/150?img=8',
      rating: 5,
      text: 'As a veteran myself, I appreciate working with a veteran-owned business. They understand the value of service and commitment. Great rates and even better customer care!'
    },
    {
      name: 'James Patterson',
      image: 'https://i.pravatar.cc/150?img=9',
      rating: 5,
      text: 'After my accident, the claims process was smooth and hassle-free. The team walked me through every step and got me back on the road quickly. Cannot thank them enough!'
    },
    {
      name: 'Linda Chen',
      image: 'https://i.pravatar.cc/150?img=10',
      rating: 5,
      text: 'Been with TQI for 3 years now. Every interaction has been professional and helpful. They truly care about their clients and it shows in their service.'
    },
    {
      name: 'Robert Williams',
      image: 'https://i.pravatar.cc/150?img=11',
      rating: 5,
      text: 'Small business owner here - their commercial insurance package is perfect for my needs. Fair pricing, comprehensive coverage, and local support. What more could you ask for?'
    },
    {
      name: 'Maria Rodriguez',
      image: 'https://i.pravatar.cc/150?img=12',
      rating: 5,
      text: 'Total Quality Insurance lives up to their name! Quick responses, knowledgeable agents, and they helped me understand my policy options. Highly satisfied customer!'
    }
  ];

  const advantages = [
    { text: 'We represent multiple insurance companies' },
    { text: 'We are licensed insurance advisors' },
    { text: 'We are by your side every step of the way' },
    { text: 'We offer a wide variety of insurance solutions' },
    { text: 'We are there for you in your time of need' }
  ];

  return (
    <div className="min-h-screen bg-white" id="main-content">
      <SEO
        title="Affordable Insurance in Augusta GA & North Augusta SC | TQI"
        description="Veteran-owned insurance agency serving Augusta GA & North Augusta SC. Compare home, auto & business quotes from 30+ carriers. Call 706-434-8184."
        keywords="insurance Augusta GA, insurance North Augusta SC, home insurance CSRA, auto insurance Augusta, business insurance SC GA, independent insurance agent, veteran-owned insurance, affordable insurance Augusta"
        canonicalUrl="https://totalqualityinsurance.com"
        reviews={testimonials}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "name": "Total Quality Insurance",
              "url": "https://totalqualityinsurance.com",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://totalqualityinsurance.com/?s={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            },
            {
              "@type": "InsuranceAgency",
              "name": "Total Quality Insurance",
              "description": "Veteran-owned independent insurance agency serving Augusta GA, North Augusta SC, and the CSRA. Compare rates from 30+ carriers for home, auto, business, and landlord insurance.",
              "url": "https://totalqualityinsurance.com",
              "telephone": "+1-706-434-8184",
              "email": "support@totalqualityinsurance.com",
              "founder": { "@type": "Person", "name": "Adam Diers", "description": "Air Force Veteran, Founder" },
              "address": { "@type": "PostalAddress", "addressLocality": "North Augusta", "addressRegion": "SC", "postalCode": "29841", "addressCountry": "US" },
              "geo": { "@type": "GeoCoordinates", "latitude": "33.5018", "longitude": "-81.9651" },
              "priceRange": "$$",
              "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "09:00", "closes": "17:00" },
              "areaServed": [
                { "@type": "State", "name": "South Carolina" },
                { "@type": "State", "name": "Georgia" }
              ],
              "sameAs": [
                "https://www.facebook.com/TQIAgency",
                "https://www.instagram.com/tqiagency/",
                "https://www.linkedin.com/company/total-quality-insurance"
              ],
              "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "bestRating": "5", "reviewCount": "382" },
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Insurance Products",
                "itemListElement": [
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Home Insurance" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Auto Insurance" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Business Insurance" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Landlord Insurance" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Workers Compensation" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "General Liability" } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "RV Insurance" } }
                ]
              }
            }
          ]
        }}
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section ref={heroRef} className="relative bg-gradient-to-br from-[#101C47] to-[#1a2d6b] text-white py-12 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.pexels.com/photos/6289100/pexels-photo-6289100.jpeg?auto=compress&cs=tinysrgb&w=1280&q=40)', backgroundSize: 'cover', backgroundPosition: 'center', transform: `translateY(${heroOffset * 0.5}px)` }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6 animate-fade-in">
              <div className="inline-flex items-center space-x-1.5 sm:space-x-2 bg-[#d32f2f] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg">
                <Shield className="text-white" size={16} />
                <span className="text-xs sm:text-sm font-bold text-white">VETERAN-OWNED</span>
              </div>
              <div className="inline-flex items-center space-x-1.5 sm:space-x-2 bg-[#d32f2f] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg">
                <Heart className="text-white" size={16} />
                <span className="text-xs sm:text-sm font-bold text-white">LOCALLY OWNED & OPERATED</span>
              </div>
              <div className="inline-flex items-center space-x-1.5 sm:space-x-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Star className="text-[#f92626]" size={16} />
                <span className="text-xs sm:text-sm font-medium">5-Star Service</span>
              </div>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6 leading-tight animate-slide-up">
              Affordable Home, Auto & Business Insurance <span className="text-[#f92626]">in Augusta GA & North Augusta SC.</span>
            </h1>
            <p className="text-base sm:text-xl mb-6 sm:mb-8 text-gray-200 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Independent insurance agents comparing 30+ top-rated carriers to find CSRA families the best coverage at the lowest rates. Veteran-owned, locally operated in North Augusta, SC.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/get-a-quote"><Button size="lg" className="bg-[#d32f2f] hover:bg-[#c62828] text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-lg hover:shadow-xl transition-all">
                Start My Quote <ArrowRight className="ml-2" size={18} />
              </Button></Link>
              <a href="tel:706-434-8184"><Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#101C47] text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-all">
                Talk to an Agent
              </Button></a>
            </div>
            <p className="mt-3 text-xs sm:text-sm text-gray-300 animate-slide-up" style={{ animationDelay: '0.25s' }} data-testid="hero-quote-microcopy">Takes about 3 minutes. No obligation.</p>
            <div className="mt-8 sm:mt-12 flex flex-wrap items-center gap-4 sm:space-x-6 text-xs sm:text-sm">
              <div className="flex items-center space-x-2">
                <CheckCircle className="text-[#f92626]" size={18} />
                <span>Over 30+ carriers</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="text-[#f92626]" size={18} />
                <span>Bundle & save up to 20%</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-10 sm:py-16 bg-white border-b-2 border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 max-w-4xl mx-auto" staggerMs={200}>
            {/* Veteran Owned Badge */}
            <Card className="overflow-hidden border-2 border-[#f92626] hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-5 sm:p-8 text-center bg-gradient-to-br from-white to-gray-50">
                <div className="mb-3 sm:mb-4 flex justify-center">
                  <div className="relative">
                    <div className="w-16 h-16 sm:w-24 sm:h-24 bg-[#101C47] rounded-full flex items-center justify-center">
                      <Shield className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#d32f2f] rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#101C47] mb-3">VETERAN-OWNED</h2>
                <div className="w-16 h-1 bg-[#f92626] mx-auto mb-4"></div>
                <p className="text-gray-700 text-sm sm:text-lg leading-relaxed">
                  Founded by an <strong>Air Force Veteran</strong> committed to serving with integrity, honor, and excellence.
                </p>
              </CardContent>
            </Card>

            {/* Locally Owned Badge */}
            <Card className="overflow-hidden border-2 border-[#f92626] hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-5 sm:p-8 text-center bg-gradient-to-br from-white to-gray-50">
                <div className="mb-3 sm:mb-4 flex justify-center">
                  <div className="relative">
                    <div className="w-16 h-16 sm:w-24 sm:h-24 bg-[#d32f2f] rounded-full flex items-center justify-center">
                      <Heart className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#101C47] rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#101C47] mb-3">LOCALLY OWNED & OPERATED</h2>
                <div className="w-16 h-1 bg-[#f92626] mx-auto mb-4"></div>
                <p className="text-gray-700 text-sm sm:text-lg leading-relaxed">
                  <strong>Proudly serving the CSRA</strong> and 13 states with personalized, community-focused insurance solutions.
                </p>
              </CardContent>
            </Card>
          </StaggerReveal>
          <RevealSection delay={400}>
          <div className="text-center mt-12">
            <p className="text-[#101C47] font-semibold text-xl">
              Supporting our community with <span className="text-[#f92626]">values-driven</span> insurance protection
            </p>
          </div>
          </RevealSection>
        </div>
      </section>

      {/* Bundle Quote Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#101C47] mb-4">Your Bundled Quote</h2>
            <p className="text-gray-600 text-lg">Personalized for your area</p>
          </div>
          </RevealSection>
          <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" staggerMs={120}>
            <Card className="hover:shadow-xl transition-shadow duration-300 border-2 hover:border-[#f92626]">
              <CardContent className="p-6 text-center">
                <Home className="w-12 h-12 text-[#f92626] mx-auto mb-4" />
                <h3 className="font-bold text-xl text-[#101C47] mb-2">Home</h3>
                <p className="text-gray-600 text-sm">Replacement, liability & more</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-xl transition-shadow duration-300 border-2 hover:border-[#f92626]">
              <CardContent className="p-6 text-center">
                <Car className="w-12 h-12 text-[#f92626] mx-auto mb-4" />
                <h3 className="font-bold text-xl text-[#101C47] mb-2">Auto</h3>
                <p className="text-gray-600 text-sm">State-compliant coverage</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-xl transition-shadow duration-300 border-2 hover:border-[#f92626]">
              <CardContent className="p-6 text-center">
                <Shield className="w-12 h-12 text-[#f92626] mx-auto mb-4" />
                <h3 className="font-bold text-xl text-[#101C47] mb-2">Umbrella</h3>
                <p className="text-gray-600 text-sm">Extra liability protection</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-xl transition-shadow duration-300 border-2 hover:border-[#f92626]">
              <CardContent className="p-6 text-center">
                <Building2 className="w-12 h-12 text-[#f92626] mx-auto mb-4" />
                <h3 className="font-bold text-xl text-[#101C47] mb-2">Business</h3>
                <p className="text-gray-600 text-sm">GL • BOP • Workers' comp</p>
              </CardContent>
            </Card>
          </StaggerReveal>
          <RevealSection delay={300}>
          <div className="text-center mt-8">
            <p className="text-[#101C47] font-semibold text-xl mb-2">Bundle & save up to <span className="text-[#f92626]">20% off</span></p>
            <p className="text-gray-500 text-sm mb-6">Discount varies by state and carrier</p>
            <Link to="/get-a-quote"><Button size="lg" className="bg-[#d32f2f] hover:bg-[#c62828] text-white px-8">
              Start My Quote
            </Button></Link>
          </div>
          </RevealSection>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <RevealSection direction="left">
              <picture>
                <source srcSet="/tqi-logo-color.webp" type="image/webp" />
                <img 
                  src="/tqi-logo-color.png" 
                  alt="Total Quality Insurance - Veteran-Owned Independent Insurance Agency in North Augusta SC" 
                  className="w-48 mb-6"
                  width="192"
                  height="74"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <h2 className="text-2xl md:text-3xl font-bold text-[#101C47] mb-4">Your Trusted Independent Insurance Agency in Augusta GA & North Augusta SC</h2>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6">
                Total Quality Insurance is a proudly <strong className="text-[#101C47]">independent insurance agency</strong> serving the Central Savannah River Area (CSRA) and clients across 13 states. Founded by an <strong className="text-[#101C47]">Air Force Veteran</strong>, our roots are built on service, integrity, and treating every client like family. Based in <strong className="text-[#101C47]">North Augusta, South Carolina</strong>, we help families and businesses in Augusta GA, Aiken SC, Evans GA, Martinez GA, and throughout the CSRA find affordable insurance coverage.
              </p>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6">
                Unlike captive agents who sell for one company, we compare quotes from <strong className="text-[#101C47]">30+ top-rated insurance carriers</strong> — giving us the flexibility to find coverage that fits your unique needs and budget. Whether you need <Link to="/services/home-insurance" className="text-[#f92626] hover:underline font-semibold">home insurance</Link>, <Link to="/services/auto-insurance" className="text-[#f92626] hover:underline font-semibold">auto insurance</Link>, <Link to="/services/business-insurance" className="text-[#f92626] hover:underline font-semibold">business insurance</Link>, or specialized coverage, our local agents deliver personalized solutions — not cookie-cutter policies.
              </p>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                CSRA families who bundle home and auto insurance with us save an average of <strong className="text-[#101C47]">15-25% on their annual premiums</strong>. Our 4.9-star Google rating from 382+ reviews is a testament to our commitment to putting clients first. <Link to="/get-a-quote" className="text-[#f92626] hover:underline font-semibold">Get a free quote today</Link> or call <a href="tel:706-434-8184" className="text-[#f92626] hover:underline font-semibold">706-434-8184</a>.
              </p>
            </RevealSection>
            <RevealSection direction="right" delay={200}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <Card className="p-4 sm:p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-l-4 border-[#f92626]">
                <Award className="w-8 h-8 sm:w-10 sm:h-10 text-[#f92626] mb-3 sm:mb-4" />
                <h3 className="font-bold text-base sm:text-lg text-[#101C47] mb-2">Expertise You Can Trust</h3>
                <p className="text-gray-600 text-sm">Years of experience for smart insurance decisions</p>
              </Card>
              <Card className="p-4 sm:p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-l-4 border-[#f92626]">
                <Users className="w-8 h-8 sm:w-10 sm:h-10 text-[#f92626] mb-3 sm:mb-4" />
                <h3 className="font-bold text-base sm:text-lg text-[#101C47] mb-2">Personalized Service</h3>
                <p className="text-gray-600 text-sm">Solutions tailored to your needs</p>
              </Card>
              <Card className="p-4 sm:p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-l-4 border-[#f92626]">
                <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-[#f92626] mb-3 sm:mb-4" />
                <h3 className="font-bold text-base sm:text-lg text-[#101C47] mb-2">Customized Coverage</h3>
                <p className="text-gray-600 text-sm">Protection that fits you perfectly</p>
              </Card>
              <Card className="p-4 sm:p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-l-4 border-[#f92626]">
                <Heart className="w-8 h-8 sm:w-10 sm:h-10 text-[#f92626] mb-3 sm:mb-4" />
                <h3 className="font-bold text-base sm:text-lg text-[#101C47] mb-2">Integrity First</h3>
                <p className="text-gray-600 text-sm">Honest, transparent, veteran-founded</p>
              </Card>
            </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* Independent Agency Advantages */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection>
          <div className="text-center mb-12">
            <p className="text-[#f92626] font-semibold mb-2">What Makes Us Unique</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#101C47] mb-4">The Advantages of an Independent Agency</h2>
            <p className="text-gray-600 text-lg">What makes our independent agency different?</p>
          </div>
          </RevealSection>
          <StaggerReveal className="max-w-3xl mx-auto space-y-4" staggerMs={100}>
            {advantages.map((advantage, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle className="text-[#f92626] flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-700 text-lg">{advantage.text}</p>
              </div>
            ))}
          </StaggerReveal>
          <RevealSection delay={500}>
          <div className="text-center mt-8">
            <Link to="/we-are-independent">
              <Button className="bg-[#101C47] hover:bg-[#0d1535] text-white px-8">
                Find Out How <ChevronRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
          </RevealSection>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="family" className="w-full">
            <TabsList className="grid w-full max-w-4xl mx-auto grid-cols-1 md:grid-cols-3 h-auto">
              <TabsTrigger value="family" className="text-left p-4 data-[state=active]:bg-[#d32f2f] data-[state=active]:text-white">
                <div>
                  <p className="font-bold text-lg mb-1">Treating You Like Family</p>
                  <p className="text-sm">Get the respect you deserve</p>
                </div>
              </TabsTrigger>
              <TabsTrigger value="clients" className="text-left p-4 data-[state=active]:bg-[#d32f2f] data-[state=active]:text-white">
                <div>
                  <p className="font-bold text-lg mb-1">Clients Come First</p>
                  <p className="text-sm">We go the extra mile</p>
                </div>
              </TabsTrigger>
              <TabsTrigger value="experienced" className="text-left p-4 data-[state=active]:bg-[#d32f2f] data-[state=active]:text-white">
                <div>
                  <p className="font-bold text-lg mb-1">Experienced Agents</p>
                  <p className="text-sm">30+ years combined experience</p>
                </div>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="family" className="mt-8 text-center">
              <p className="text-gray-700 text-lg max-w-3xl mx-auto">
                Get the respect you deserve from an agency that treats you like family and is here to help you.
              </p>
            </TabsContent>
            <TabsContent value="clients" className="mt-8 text-center">
              <p className="text-gray-700 text-lg max-w-3xl mx-auto">
                We go the extra mile to exceed your expectations, no matter what your situation or needs are.
              </p>
            </TabsContent>
            <TabsContent value="experienced" className="mt-8 text-center">
              <p className="text-gray-700 text-lg max-w-3xl mx-auto">
                With over 30 years of combined experience, we understand what it takes to bring you the coverage you need.
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection>
          <div className="text-center mb-12">
            <p className="text-[#f92626] font-semibold mb-2">Our Specialties</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#101C47] mb-4">Insurance Services in Augusta GA & North Augusta SC</h2>
          </div>
          </RevealSection>
          <StaggerReveal className="grid grid-cols-1 md:grid-cols-3 gap-8" staggerMs={150}>
            {services.map((service, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={`${service.title} in Augusta GA and North Augusta SC - Total Quality Insurance`} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#101C47] to-transparent opacity-60"></div>
                </div>
                <CardContent className="p-6">
                  <service.icon className="w-10 h-10 text-[#f92626] mb-4" />
                  <h3 className="text-xl font-bold text-[#101C47] mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link to={service.link}>
                    <Button variant="ghost" className="text-[#f92626] hover:text-[#d91f1f] p-0">
                      Learn About {service.title} <ChevronRight className="ml-1" size={16} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          <RevealSection delay={300}>
          <div className="text-center mt-8">
            <Link to="/get-a-quote"><Button size="lg" className="bg-[#d32f2f] hover:bg-[#c62828] text-white px-8">
              Start My Quote →
            </Button></Link>
          </div>
          </RevealSection>
          </StaggerReveal>
        </div>
      </section>

      {/* Video Testimonials */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection>
          <div className="text-center mb-12">
            <p className="text-[#f92626] font-semibold mb-2">Real Stories from Real Clients</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#101C47] mb-4">Video Testimonials</h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Hear directly from our clients about their experience with Total Quality Insurance.
            </p>
          </div>
          </RevealSection>
          <StaggerReveal className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto" staggerMs={150}>
            {[
              { id: '5mQIMma-ySE', title: 'Total Quality Insurance Client Testimonial - Home Insurance Review Augusta GA' },
              { id: '0D5QliNDiss', title: 'Total Quality Insurance Client Testimonial - Auto Insurance Review North Augusta SC' },
              { id: 'XpquFUXxQvU', title: 'Total Quality Insurance Client Testimonial - Business Insurance Review CSRA' },
              { id: 'VCNbE_UzSzk', title: 'Total Quality Insurance Client Testimonial - Bundle Insurance Savings Review' }
            ].map((video, index) => (
              <YouTubeFacade key={index} videoId={video.id} title={video.title} />
            ))}
          </StaggerReveal>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection>
          <div className="text-center mb-12">
            <p className="text-[#f92626] font-semibold mb-2">What Our Clients Have to Say</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#101C47] mb-4">Written Testimonials</h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              We take pride in delivering excellent service and building lasting relationships with our clients.
            </p>
          </div>
          </RevealSection>
          <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerMs={80}>
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={`${testimonial.name} - Total Quality Insurance client review in Augusta GA`} 
                    className="w-12 h-12 rounded-full"
                    loading="lazy"
                    decoding="async"
                  />
                  <div>
                    <p className="font-semibold text-[#101C47]">{testimonial.name}</p>
                    <div className="flex space-x-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} size={16} className="fill-[#f92626] text-[#f92626]" />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 italic">"{testimonial.text}"</p>
              </Card>
            ))}
          </StaggerReveal>
          <div className="text-center mt-12">
            <div className="inline-flex items-center space-x-3 bg-gray-50 px-6 py-3 rounded-lg">
              <p className="text-[#101C47] font-semibold">Great</p>
              <div className="flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} className="fill-[#f92626] text-[#f92626]" />
                ))}
              </div>
              <p className="text-gray-600">4.9 out of 5 based on <strong>382 reviews</strong></p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-20 bg-gradient-to-br from-[#101C47] to-[#1a2d6b] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <RevealSection direction="left">
              <p className="text-[#f92626] font-semibold mb-2">Insurance On The Go</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">Download Our Mobile App</h2>
              <p className="text-base sm:text-xl text-gray-200 mb-6 sm:mb-8">
                Manage your communication with our Agency, file claims, and get instant support - all from your mobile device. Your insurance protection, always in your pocket.
              </p>
              
              {/* Features List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#f92626] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">Quick Quotes</h3>
                    <p className="text-sm text-gray-300">Compare 30+ carriers in minutes</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#f92626] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">File Claims</h3>
                    <p className="text-sm text-gray-300">Submit claims quickly and easily</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#f92626] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">Pay Bills</h3>
                    <p className="text-sm text-gray-300">Secure payment processing</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#f92626] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">24/7 Support</h3>
                    <p className="text-sm text-gray-300">Talk or text with agents anytime</p>
                  </div>
                </div>
              </div>

              {/* Download Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a 
                  href="#" 
                  className="inline-flex items-center justify-center bg-black hover:bg-gray-900 text-white px-6 py-3 rounded-lg transition-all hover:scale-105 shadow-lg"
                >
                  <svg className="w-8 h-8 mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">Download on the</div>
                    <div className="text-lg font-semibold">App Store</div>
                  </div>
                </a>
                <a 
                  href="#" 
                  className="inline-flex items-center justify-center bg-black hover:bg-gray-900 text-white px-6 py-3 rounded-lg transition-all hover:scale-105 shadow-lg"
                >
                  <svg className="w-8 h-8 mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">Get it on</div>
                    <div className="text-lg font-semibold">Google Play</div>
                  </div>
                </a>
              </div>
            </RevealSection>

            {/* Right Phone Mockup */}
            <RevealSection direction="right" delay={200}>
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Floating elements decoration */}
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#f92626] rounded-full opacity-20 blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white rounded-full opacity-10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                
                {/* Phone Image */}
                <div className="relative z-10 mx-auto" style={{ maxWidth: '320px' }}>
                  <div className="absolute inset-0 bg-black/20 blur-2xl transform translate-y-4 rounded-[3rem]"></div>
                  <img 
                    src="/tqi_app_nobg.png"
                    alt="Total Quality Insurance Mobile App - Get quotes, file claims, pay bills and manage policies on your phone"
                    className="relative w-full h-auto drop-shadow-2xl"
                    data-testid="mobile-app-image"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* Client Center */}
      <section className="py-20 bg-[#101C47] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Manage Your Account Now</h2>
            <p className="text-gray-200 text-lg">How can Total Quality Insurance help?</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Report a Claim', desc: 'Get support in filing your claim' },
              { title: 'Policy Change', desc: 'Fine tune your policy options' },
              { title: 'Request Certificate', desc: 'Get proof of your insurance quickly' },
              { title: 'Auto ID Card', desc: 'Lose your card? Get a new one' },
              { title: 'Pay Bill', desc: 'Make payments securely online' },
              { title: 'Policy Review', desc: 'Compare your coverage with your needs' }
            ].map((item, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all p-6 cursor-pointer">
                <h3 className="font-bold text-lg mb-2 text-white">{item.title}</h3>
                <p className="text-gray-200 text-sm">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <RevealSection direction="left">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#101C47] mb-6">Contact Your Local Insurance Agent in Augusta GA & North Augusta SC</h2>
              <p className="text-gray-700 text-lg mb-8">As an independent agency serving the CSRA, we're here to help you compare rates from 30+ carriers and find the right coverage at the best price.</p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <Phone className="text-[#f92626] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-[#101C47]">Call Us</p>
                    <a href="tel:706-434-8184" className="text-gray-600 hover:text-[#f92626] transition-colors">706-434-8184</a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Mail className="text-[#f92626] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-[#101C47]">Email Us</p>
                    <a href="mailto:support@totalqualityinsurance.com" className="text-gray-600 hover:text-[#f92626] transition-colors">support@totalqualityinsurance.com</a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <MapPin className="text-[#f92626] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-[#101C47]">Visit Us</p>
                    <p className="text-gray-600">Central Savannah River Area (CSRA)</p>
                  </div>
                </div>
              </div>
            </div>
            </RevealSection>
            <RevealSection direction="right" delay={200}>
            <Card className="p-8">
              <form className="space-y-4" onSubmit={handleContactSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="First Name" value={contactForm.firstName} onChange={e => setContactForm({...contactForm, firstName: e.target.value})} className="border-gray-300 h-12 text-base" data-testid="contact-first-name" />
                  <Input placeholder="Last Name" value={contactForm.lastName} onChange={e => setContactForm({...contactForm, lastName: e.target.value})} className="border-gray-300 h-12 text-base" data-testid="contact-last-name" />
                </div>
                <Input placeholder="Email" type="email" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} className="border-gray-300 h-12 text-base" data-testid="contact-email" />
                <Input placeholder="Phone" type="tel" value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} className="border-gray-300 h-12 text-base" data-testid="contact-phone" />
                <select className="w-full p-3 border border-gray-300 rounded-md text-base h-12" value={contactForm.insuranceType} onChange={e => setContactForm({...contactForm, insuranceType: e.target.value})} data-testid="contact-insurance-type" aria-label="Type of insurance">
                  <option value="">Type of insurance</option>
                  <option>Home Insurance</option>
                  <option>Auto Insurance</option>
                  <option>Business Insurance</option>
                  <option>Landlord / Rental Insurance</option>
                  <option>Workers' Compensation</option>
                  <option>General Liability</option>
                  <option>RV Insurance</option>
                  <option>Something Else</option>
                </select>
                <textarea 
                  placeholder="Tell us about your insurance needs..." 
                  rows="4" 
                  value={contactForm.message}
                  onChange={e => setContactForm({...contactForm, message: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-md text-base"
                  data-testid="contact-message"
                ></textarea>
                <Button type="submit" disabled={contactSubmitting} className="w-full bg-[#d32f2f] hover:bg-[#c62828] text-white h-12 text-base" data-testid="contact-submit-btn">
                  <Send className="mr-2" size={16} />{contactSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </Card>
            </RevealSection>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;